// ---------------------------------------------------------------------------
// External links in rendered rich bodies open in a NEW tab. Comment, note and
// description HTML lands via innerHTML inside `.cg-prose` containers, so its
// anchors carry no target and a click navigated the whole app away. One
// delegated capture-phase listener covers every body ever rendered — including
// ones that don't exist yet — instead of per-component target rewriting.
// Same-origin links (e.g. pasted task links like /#/t/123) keep navigating
// in place: the router owns those.
// ---------------------------------------------------------------------------

/** True when `href` resolves to an http(s) URL on a DIFFERENT origin than
 *  `origin`. Non-http(s) schemes (mailto:, tel:) are not "external" here —
 *  their native behavior is already the right one. Pure: testable under node. */
export function isExternalHref(href: string, origin: string): boolean {
  let u: URL;
  // Relative hrefs resolve against the app's own origin — same-origin by
  // construction, so they fall out via the origin comparison below.
  try { u = new URL(href, origin); } catch { return false; }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
  return u.origin !== origin;
}

/** Install the document-level handler. Returns the uninstaller (effect-cleanup
 *  shape). Guarded for non-browser environments: this module must be loadable
 *  under node (tests, SSR) where `document` does not exist. */
export function installExternalLinkHandler(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const onClick = (e: MouseEvent) => {
    // Modified/secondary clicks keep their native meaning (browser-managed
    // new tab/window); only a plain left click is redirected.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target instanceof Element ? e.target : null)?.closest('a[href]');
    if (!(a instanceof HTMLAnchorElement) || !a.closest('.cg-prose')) return;
    // RichEditor's editing surface is `.cg-prose` too — a click on a link in a
    // DRAFT is cursor placement, not link following.
    if (a.isContentEditable) return;
    if (!isExternalHref(a.href, window.location.origin)) return;
    e.preventDefault();
    // Capture-phase stop: without it the click still bubbles into container
    // handlers with their own meaning — the task description's wrapper flips
    // into edit mode on any click, which must not happen under a link-follow.
    e.stopPropagation();
    window.open(a.href, '_blank', 'noopener,noreferrer');
  };
  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}
